import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { BatchRecord } from 'src/app/controls/inline-editable-table/models/BatchRecord';
import { BatchCreate } from 'src/app/controls/inline-editable-table/models/batch-create';
import { BatchDelete } from 'src/app/controls/inline-editable-table/models/batch-delete';
import { BatchModification } from 'src/app/controls/inline-editable-table/models/batch-modifications';
import { BatchUpdate } from 'src/app/controls/inline-editable-table/models/batch-update';
import { HttpError } from 'src/app/core/http-error';
import { Attribute } from 'src/app/models/attribute';
import { AttributeValue } from 'src/app/models/attribute-value';
import { Rights } from 'src/app/models/enums/rights';
import { IAttribute } from 'src/app/models/interfaces/attribute.interface';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { AttributesValuesService } from 'src/app/services/attributes-values.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { AttributeDetailContext } from './attribute-detail-context';
import { AttributeDetailComponent } from './attribute-detail/attribute-detail.component';
import { AttributesListComponent } from './attributes-list/attributes-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-attributes',
    templateUrl: './attributes.component.html',
    styleUrls: ['./attributes.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, AttributesListComponent, DetailHeaderComponent, AttributeDetailComponent],
})
export class AttributesComponent extends DefaultManagementComponent<IAttribute, Attribute, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faAttribute = faTags;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre Attribute.
     */
    public defaultContext: AttributeDetailContext = new AttributeDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Reference au composant BoxesList.
     */
    @ViewChild(AttributesListComponent) attributesList: AttributesListComponent;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param AttributesService : Service métier utilisé pour gérer les Attribute.
     */
    constructor(
        protected override notificationService: NotificationService,
        private attributesService: AttributeDefinitionsService,
        private attributeValuesService: AttributesValuesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.AttributCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.AttributEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param attribute attribute selectionné.
     */
    public onAttributeSelected(attribute: Attribute) {
        this.spinner.show('main-spinner');
        this.loadAttribute(attribute.id).subscribe(
            (x) => {
                this.refresh(Attribute.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'attribut. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param attribute Attribute sélectionné.
     */
    public onAttributeDisplayed(attribute: Attribute) {
        this.spinner.show('main-spinner');
        this.loadAttribute(attribute.id).subscribe(
            (x) => {
                this.refresh(Attribute.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'attribut. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param attribute Attribute sélectionné.
     */
    public onAttributeEdited(attribute: Attribute) {
        this.spinner.show('main-spinner');
        // Cas de la mise à jour.
        this.loadAttribute(attribute.id).subscribe(
            (x) => {
                this.refresh(Attribute.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer l'attribut. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onAttributeCreated() {
        this.displayForm(Attribute.createFormCopy(new Attribute()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param attribute Attribute sélectionné.
     */
    public onAttributeDuplicated(attribute: Attribute) {
        let duplicatedAttribute = Attribute.createFormCopy(attribute);
        duplicatedAttribute.id = null;
        duplicatedAttribute.custom = true;
        this.displayForm(duplicatedAttribute, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param Attribute : Valeur du client retourné par le formulaire.
     */
    public onAttributeUpdated(attribute: Attribute): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            console.log('Updated attribute : ', attribute);
            this.attributesService.update(this.context.editedItemId, attribute).subscribe(
                (x) => {
                    this.refreshWithNotification(Attribute.createFormCopy(x), 'Attribut mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'attribut. " + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.attributesService.add(attribute).subscribe(
                (x) => {
                    this.refreshWithNotification(Attribute.createFormCopy(x), 'Attribut créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer l'attribut. " + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque les valeurs d'un attribut sont sauvegardés.
     * @param attributeValues : Valeurs de l'attribut.
     */
    public onAttributeValuesUpdated(attributeValues: BatchRecord<AttributeValue>[]): void {
        if (this.context.editedItemId) {
            this.spinner.show('main-spinner');

            let modifications = new BatchModification<AttributeValue>();

            attributeValues.forEach((x) => {
                if (x.isDeleted) {
                    if ((x.data.editableId as number) > 0) {
                        modifications.deletes.push(new BatchDelete(x.data));
                    }
                } else if (x.isNew) {
                    modifications.creates.push(new BatchCreate(x.data));
                } else if (x.isEdited) {
                    modifications.updates.push(new BatchUpdate(x.data));
                }
            });

            if (modifications.deletes.length > 0 || modifications.updates.length > 0 || modifications.creates.length > 0) {
                this.attributeValuesService.applyBatchModifications(this.context.editedItemId, modifications).subscribe(
                    (x) => {
                        console.log(x);
                        this.spinner.hide('main-spinner');

                        let errors = x.filter((record) => record.result === false);

                        if (errors.length === 0) {
                            this.notificationService.displaySuccess(null, "Les valeurs de l'attribut ont bien été sauvegardées");
                        } else if (errors.length === x.length) {
                            let errorMessage = "Les valeurs de l'attribut n'ont pas pu être sauvegardées :";

                            errors.forEach((err) => {
                                errorMessage = errorMessage.concat(' ', err.errorMessage, err.error.message ? err.error.message : '');
                                console.log(errorMessage, err.error);
                            });

                            this.notificationService.displayError(null, errorMessage);
                        } else {
                            let errorMessage = "Certaines valeurs de l'attribut n'ont pas pu être sauvegardées :";

                            errors.forEach((err) => {
                                errorMessage = errorMessage.concat(' ', err.errorMessage, err.error.message ? err.error.message : '');
                                console.log(errorMessage, err.error);
                            });

                            this.notificationService.displayWarning(null, errorMessage);
                        }

                        this.onAttributeSelected(this.context.formContext.item as Attribute);
                    },
                    (error: HttpError) => {
                        this.notificationService.displayError('Erreur', "Impossible de sauver les valeurs de l'attribut. " + error.message);
                        this.spinner.hide('main-spinner');
                    }
                );
            } else {
                // Aucune modif
                this.spinner.hide('main-spinner');
            }
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param attribute Attribute sélectionné.
     */
    public onAttributeDeleted(attribute: Attribute) {
        this.spinner.show('main-spinner');
        // Suppression effective de l'Attribute.
        this.attributesService.delete(attribute.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Attribut supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de supprimer l'attribut. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appelé lors de l'export.
     */
    public onAttributesExported() {
        this.createContext();
        this.itemsList.export('attributs.xlsx');
    }

    /**
     * Appelé lors de l'import.
     */
    public onAttributesImported() {
        this.itemsList.refresh();
    }

    /**
     * Appelé lors de l'aide.
     */
    public onAttributesHelped() {
        this.itemsList.displayHelpDialog('attributes');
    }

    onNeedAttributeValuesRefreshed(attributeId: number) {
        this.spinner.show('main-spinner');
        this.loadAttribute(attributeId).subscribe(
            (x) => {
                let context = this.createContext();
                context.formContext.item = Attribute.createFormCopy(x);
                context.editedItemId = attributeId;
                context.formMode = FormMode.Edition;
                context.formContext.formVisible = true;
                context.formContext.mode = FormMode.Edition;
                this.context = context;

                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de raffraichir les valeurs de l'attribut. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'un Attribute.
     * @param attributeId : Id de l'attribut à charger.
     */
    public loadAttribute(attributeId: number): Observable<Attribute> {
        return this.attributesService.get(attributeId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param Attribute : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    public fillForm(Attribute: IAttribute): AttributeDetailContext {
        let context = this.createContext();

        context.formContext.item = Attribute;
        context.editedItemId = Attribute.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): AttributeDetailContext {
        let context = new AttributeDetailContext();
        return context;
    }

    public onAttributesSorted(attributes: Attribute[]) {
        this.spinner.show('main-spinner');
        this.attributesService.applyOrderChanges(attributes).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'ordre des attributs a été appliqué");
                this.attributesList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'appliquer l'ordre des attributs. " + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }
}
